import { ReactNode, useContext, useRef, useState } from "react";
import {
  AddIcon,
  Button,
  FlexContent,
  InlineOrderEditor,
  ModalDialog,
  TableCell,
  TableHeadCell,
  TableRow,
  useDebounce,
} from "@rothoblaas/design-system";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useInitializer from "../../hooks/useInitializer";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { Paginated, PaginationQuery } from "../../types";
import { getDeleteAction, getEditAction } from "../../utils/rowActions";
import Icon from "./Icon";
import Table from "./Table";
import Visibility from "./Visibility";
import { VisibilityScopeValue } from "./VisibilityScope";

type VisibilityAndOrder = { visibility: VisibilityScopeValue; order: number };

type VisibilityAndOrderProps<T> = {
  updateVisibility: (item: T, visibility: VisibilityScopeValue) => Promise<void>;
  updateOrder: (item: T, order: number) => Promise<void>;
};

type FormProps<T> =
  | {
      /** Shows the add button, which opens the form without an item. */
      canAdd: true;
      renderForm: (item: T | undefined, onClose: () => void) => ReactNode;
    }
  | {
      canAdd?: false;
      renderForm: (item: T, onClose: () => void) => ReactNode;
    }
  | {
      canAdd?: false;
      renderForm?: undefined;
    };

type DeleteProps<T> =
  | {
      /** Adds the delete action, confirmed in a dialog titled `deleteTitle`. */
      onDelete: (item: T) => Promise<void>;
      deleteTitle: string;
    }
  | { onDelete?: undefined; deleteTitle?: undefined };

type AdminCrudTableProps<T> = FormProps<T> &
  DeleteProps<T> & {
    /** Prefix of the list's URL search params (`<prefix>_q`, `<prefix>_page`, ...). */
    filterPrefix: string;
    /** SWR list hook of the entity, called with the debounced search. */
    useList: (query: PaginationQuery, signal: AbortSignal) => { data?: Paginated<T>; error?: unknown };
    /** Head cells of the entity columns: the visibility, order and actions ones are appended. */
    head: ReactNode;
    renderRow: (item: T) => ReactNode;
    /** For items with visibility and order: appends their inline editors, saved through these calls. */
    visibilityAndOrder?: T extends VisibilityAndOrder ? VisibilityAndOrderProps<T> : never;
    /** For fixed catalog entries with visibility only: appends the inline editor. */
    updateVisibility?: T extends { visibility: VisibilityScopeValue }
      ? (item: T, visibility: VisibilityScopeValue) => Promise<void>
      : never;
    /** For child rows with order only: appends the inline editor. */
    updateOrder?: T extends { order: number } ? (item: T, order: number) => Promise<void> : never;
  };

// Searchable, paged admin list with edit (and optionally add/delete) through the entity's form dialog.
const AdminCrudTable = <T,>(props: AdminCrudTableProps<T>) => {
  const { filterPrefix, useList, head, renderRow, onDelete } = props;
  const visibilityAndOrder = props.visibilityAndOrder as VisibilityAndOrderProps<T & VisibilityAndOrder> | undefined;
  const updateVisibility = props.updateVisibility ?? visibilityAndOrder?.updateVisibility;
  const updateOrder = props.updateOrder ?? visibilityAndOrder?.updateOrder;
  const canEdit = !!props.renderForm;
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ isSearchable: true, prefix: filterPrefix });
  const aborter = useRef(new AbortController());
  const [itemToEdit, setItemToEdit] = useState<T>();
  const [itemToDelete, setItemToDelete] = useState<T>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(filters.state.q);
  const query: PaginationQuery = { ...filters.state, q: debouncedValue };
  const { data, error } = useList(query, aborter.current.signal);
  const isInitialized = useInitializer(data || error);

  const save = (action: Promise<void>) =>
    action
      .then(() => dispatch(showSuccessNotification(i18n.translation)))
      .catch((err) => dispatch(showErrorNotification(i18n, err)));

  const deleteItem = (item: T) => {
    if (!onDelete) {
      return;
    }

    setIsSubmitting(true);
    onDelete(item)
      .then(() => {
        dispatch(showSuccessNotification(i18n.translation));
        setItemToDelete(undefined);
      })
      .catch((err) => dispatch(showErrorNotification(i18n, err)))
      .finally(() => setIsSubmitting(false));
  };

  if (!data && !error && !isInitialized) {
    return null;
  }

  return (
    <FlexContent padding>
      <Table<T>
        filters={filters}
        search={{
          placeholder: `${i18n.translation.common.search}...`,
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        headingActions={
          props.canAdd && (
            <Button onClick={() => setIsCreateOpen(true)}>
              <Icon glyph={AddIcon} />
              {i18n.translation.common.add}
            </Button>
          )
        }
        head={
          <TableRow>
            {head}
            {updateVisibility && <TableHeadCell align="left">{i18n.translation.common.visibility}</TableHeadCell>}
            {updateOrder && <TableHeadCell align="center">{i18n.translation.common.order}</TableHeadCell>}
            {(canEdit || onDelete) && <TableHeadCell align="left"></TableHeadCell>}
          </TableRow>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            {renderRow(item)}
            {updateVisibility && (
              <TableCell>
                <Visibility
                  value={(item as T & VisibilityAndOrder).visibility}
                  onChange={(visibility) => save(updateVisibility(item as T & VisibilityAndOrder, visibility))}
                />
              </TableCell>
            )}
            {updateOrder && (
              <TableCell align="center">
                <InlineOrderEditor
                  order={(item as T & { order: number }).order}
                  onUpdate={(order) => save(updateOrder(item as T & VisibilityAndOrder, order))}
                />
              </TableCell>
            )}
          </>
        )}
        rowActions={
          canEdit || onDelete
            ? (item) => [
                ...(canEdit ? [getEditAction(i18n, () => setItemToEdit(item))] : []),
                ...(onDelete ? [getDeleteAction(i18n, () => setItemToDelete(item), true)] : []),
              ]
            : undefined
        }
        onRowClick={canEdit ? (item) => setItemToEdit(item) : undefined}
        total={data?.total}
        error={!!error}
      />

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={props.deleteTitle}
          isSubmitting={isSubmitting}
          buttons={[
            <Button key={0} onClick={() => setItemToDelete(undefined)} variant="secondary">
              {i18n.translation.common.cancel}
            </Button>,
            <Button key={1} onClick={() => deleteItem(itemToDelete)}>
              {i18n.translation.common.yes}
            </Button>,
          ]}
        >
          {i18n.translation.clients.questions.delete}
        </ModalDialog>
      )}

      {isCreateOpen && props.canAdd && props.renderForm(undefined, () => setIsCreateOpen(false))}
      {!!itemToEdit && props.renderForm?.(itemToEdit, () => setItemToEdit(undefined))}
    </FlexContent>
  );
};

export default AdminCrudTable;
